import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, Injector, OnDestroy, OnInit, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { FormsUtil, MbsPopupType, SidepanelCrudBase, ToastService } from 'mbs-ui-kit';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { GlobalAgentOptionsTypes } from 'mbs-ui-shared@models/AgentOptions';
import Computer, { AgentType } from 'mbs-ui-shared@models/Computer';
import {
  ConnectSettingsForm,
  ConnectSettingsScope,
  IConnectSettings,
  ProxyMode
} from 'mbs-ui-shared@models/connect/connect-settings.models';
import { ConnectSettingsWrapper } from 'mbs-ui-shared@models/connect/connect-settings.wrapper';
import { SidepanelId } from 'mbs-ui-shared@models/sidepanel';
import { AgentOptionsService } from 'mbs-ui-shared@services/agent-options.service';
import { ConnectService } from 'mbs-ui-shared@services/connect.service';
import { RmCommandsWrapper } from 'mbs-ui-shared@services/rm-commands.wrapper';
import { Observable, combineLatest, mergeMap, noop, of } from 'rxjs';

import { AlertComponent, ButtonComponent, GroupButtonComponent, SidepanelComponent } from 'mbs-ui-kit';

import { ConnectSettingsComponent } from 'mbs-ui-shared@components/connect-settings/connect-settings.component';

import { CommonModule } from '@angular/common';
import { CanPipe } from 'libs/ability/src/lib/ability.pipe';
import { ComputerOsIconPipe } from 'mbs-ui-shared@utils/pipes/os-icon.pipe';
import { catchError, filter, first, map, startWith, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-sidepanel-connect-settings',
  templateUrl: './sidepanel-connect-settings.component.html',
  imports: [
    AlertComponent,
    ButtonComponent,
    CommonModule,
    ConnectSettingsComponent,
    GroupButtonComponent,
    SidepanelComponent,
    CanPipe,
    ComputerOsIconPipe,
    I18NextPipe
  ]
})
export class SidepanelConnectSettingsComponent extends SidepanelCrudBase<Computer> implements OnInit, OnDestroy {
  private companyFacade = inject(CompaniesFacade);
  private injector = inject(Injector);
  private i18nPipe = inject(I18NextPipe);
  private connectService = inject(ConnectService);
  private rmCommands = inject(RmCommandsWrapper);
  agentOptions = inject(AgentOptionsService);
  private toastService = inject(ToastService);

  public readonly alertType = MbsPopupType;
  public readonly agentType = AgentType;
  public readonly SidepanelId = SidepanelId;

  public computer: Computer;
  public isGlobalOptionsLoaded = false;
  public isOptionsLoaded = false;
  public formState: ConnectSettingsWrapper = new ConnectSettingsWrapper();
  public parentAgentSettingsFormState: ConnectSettingsWrapper = new ConnectSettingsWrapper();

  public form = this.formState.configureSettingsForm();
  public useGlobal = new FormControl(true);

  private valid: boolean;
  private globalAgentSettings: IConnectSettings;

  constructor() {
    super();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  updateData(computer: Computer): void {
    this.isOptionsLoaded = false;
    this.computer = computer;

    if (!this.isGlobalOptionsLoaded) {
      this.loadGlobalOptions();
    } else {
      const hasCompany = Boolean(this.computer.company?.id);
      this.loadOptions(hasCompany);
    }
  }

  ngOnInit(): void {
    combineLatest([
      this.form.get('managedSystem.allowRemoteConnection').valueChanges.pipe(startWith(null)),
      this.form.get('disconnectIfInactive.enabled').valueChanges.pipe(startWith(null))
    ])
      .pipe(untilDestroyed(this))
      .subscribe(this.updateFormDisabledState.bind(this));

    this.useGlobal.valueChanges.pipe(untilDestroyed(this)).subscribe(this.onUseGlobalChange.bind(this));
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(this.onConnectAgentSettingsFormChange.bind(this));
  }

  public getGlobalOptionsEditUrl(): string {
    if (this.hasCompanySettings()) {
      return `/AP/Companies/${this.computer?.company?.id}?tab=connectAgentOptions`;
    }

    return `${RoutingPath.ApGlobalAgentOptions}?active=${GlobalAgentOptionsTypes.RA}`;
  }

  public hasCompanySettings(): boolean {
    return this.formState.hasCompanySettings;
  }

  private loadGlobalOptions(): void {
    this.connectService
      .getSettings(ConnectSettingsScope.Global)
      .pipe(
        mergeMap((settings) => {
          return this.agentOptions.getDefault().pipe(
            map((options) => {
              if (settings) {
                const newSettings = new ConnectSettingsWrapper();
                return newSettings.migrateSettings(settings, options);
              }

              const newSettings = new ConnectSettingsWrapper();
              newSettings.updateState({
                managedSystem: options.connectValues
              });
              return newSettings.getSettings();
            })
          );
        }),
        untilDestroyed(this)
      )
      .subscribe((globalAgentSettings) => {
        const hasCompany = Boolean(this.computer.company?.id);
        this.globalAgentSettings = globalAgentSettings;
        this.isGlobalOptionsLoaded = true;
        this.loadOptions(hasCompany);
      });
  }

  private loadOptions(hasCompany: boolean): void {
    combineLatest([
      of(this.globalAgentSettings),
      hasCompany ? this.getCompanySettings() : of(null),
      this.connectService.getSettings(ConnectSettingsScope.Computer, this.computer.hid)
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([globalAgentSettings, companyAgentSettings, computerAgentSettings]) => {
        this.formState.hasCompanySettings = !!companyAgentSettings;
        this.parentAgentSettingsFormState.updateStateFromConfig(companyAgentSettings ? companyAgentSettings : globalAgentSettings);
        this.formState.updateStateFromConfig(computerAgentSettings);
        this.form.patchValue(this.formState, { emitEvent: false });

        this.isOptionsLoaded = true;
        this.useGlobal.patchValue(!computerAgentSettings);
      });
  }

  private getCompanySettings() {
    return this.connectService.getSettings(ConnectSettingsScope.Company, this.computer.company.id).pipe(
      mergeMap((settings) => {
        if (!settings) return of(null);
        // Tricky. If we have settings need to apply migration. Else return null, means we need to use global
        this.companyFacade.loadById({ id: this.computer.company?.id });
        return toObservable(this.companyFacade.entityById(this.computer.company?.id), { injector: this.injector }).pipe(
          filter(Boolean),
          first(),
          mergeMap((company) => {
            return company?.agentSettingsId
              ? this.agentOptions.getByKey(company.agentSettingsId).pipe(
                  map((options) => {
                    return new ConnectSettingsWrapper().migrateSettings(settings, options);
                  })
                )
              : of(settings);
          })
        );
      }),
      untilDestroyed(this)
    );
  }

  updateFormValues(): void {
    this.useGlobal.patchValue(this.formState.useGlobal, { emitEvent: false });
    if (this.formState.useGlobal) {
      this.form.patchValue(this.parentAgentSettingsFormState, { emitEvent: false });
    } else {
      this.form.patchValue(this.formState, { emitEvent: false });
    }
  }

  handleSave(): Observable<boolean> {
    if (!this.valid) {
      this.triggerValidation();

      return of(false);
    }

    this.getSaveConnectSettingsCommand(this.formState)
      .pipe(
        switchMap(() =>
          this.rmCommands.updateConnectConfig(
            {
              hid: this.computer.hid,
              online: true
            },
            AgentType.RA
          )
        ),
        map(() => {
          this.toastService.success();
          this.close.emit();

          return true;
        })
      )
      .subscribe(noop);

    return of(true);
  }

  private getSaveConnectSettingsCommand(state: ConnectSettingsWrapper): Observable<unknown> {
    if (state.useGlobal) {
      return this.connectService.deleteSettings(ConnectSettingsScope.Computer, this.computer.hid).pipe(
        catchError((e: HttpErrorResponse) => {
          if (e.status === HttpStatusCode.NotFound) {
            return of(true);
          } else {
            throw e;
          }
        })
      );
    }

    return this.connectService.updateSettings(this.formState.getSettings(), ConnectSettingsScope.Computer, this.computer.hid);
  }

  handleDelete(): Observable<boolean> {
    throw Error('NotImplement');
  }

  getComputerName(): string {
    return Computer.getComputerName(this.computer);
  }

  getComputerStatus(): string {
    return Computer.getComputerStatusText(this.computer?.online, this.i18nPipe);
  }

  isProxyServerHostVisible(): boolean {
    const formState = this.getFormState();

    if (formState) {
      const proxyMode = formState.proxy.mode;

      if (proxyMode === ProxyMode.Manual) {
        return true;
      }
    }

    return false;
  }

  isUseBasicAuthUserPasswordSectionVisible(): boolean {
    const formState = this.getFormState();

    if (formState) {
      return formState.proxy.useBasicAuth;
    }

    return false;
  }

  isUnattendedAccessPasswordVisible(): boolean {
    const formState = this.getFormState();

    if (formState) {
      return formState.security.customPassword.enabled;
    }

    return false;
  }

  isConfigPasswordVisible(): boolean {
    const formState = this.getFormState();

    if (formState) {
      return formState.configPassword.enabled;
    }

    return false;
  }

  isGlobalSettingsVisible(): boolean {
    return this.formState.useGlobal;
  }

  getFormState(): ConnectSettingsWrapper {
    return this.formState.useGlobal ? this.parentAgentSettingsFormState : this.formState;
  }

  updateFormDisabledState(): void {
    this.form.enable({ emitEvent: false });
    this.useGlobal.enable({ emitEvent: false });

    if (this.formState.useGlobal) {
      this.form.disable({ emitEvent: false });
    } else {
      const remoteAccessAllowedControl = this.form.get('managedSystem.allowRemoteConnection');

      if (remoteAccessAllowedControl.value) {
        this.form.enable({ emitEvent: false });

        if (this.form.value?.disconnectIfInactive?.enabled) {
          this.form.get('disconnectIfInactive.interval').enable();
          this.form.get('disconnectIfInactive.intervalUnits').enable();
        } else {
          this.form.get('disconnectIfInactive.interval').disable();
          this.form.get('disconnectIfInactive.intervalUnits').disable();
        }
      } else {
        this.form.disable({ emitEvent: false });
        remoteAccessAllowedControl.enable({ emitEvent: false });
      }
    }
  }

  onConnectAgentSettingsFormChange(data: ConnectSettingsForm['value']): void {
    this.formState.updateState(data);

    this.updateValidState();
  }

  isLoading(): boolean {
    return !this.isGlobalOptionsLoaded || !this.isOptionsLoaded;
  }

  onUseGlobalChange(useGlobal: boolean): void {
    this.formState.useGlobal = useGlobal;

    this.updateFormValues();
    this.updateFormDisabledState();
    this.updateValidState();
  }

  triggerValidation(): void {
    if (this.isProxyServerHostVisible()) {
      FormsUtil.triggerValidation(this.form.get('proxy.host'));
    }

    if (this.isUseBasicAuthUserPasswordSectionVisible()) {
      FormsUtil.triggerValidation(this.form.get('proxy.basicAuthUserName'));
      FormsUtil.triggerValidation(this.form.get('proxy.basicAuthPassword'));
    }

    if (this.isConfigPasswordVisible()) {
      FormsUtil.triggerValidation(this.form.get('configPassword.password'));
    }

    if (this.isUnattendedAccessPasswordVisible()) {
      FormsUtil.triggerValidation(this.form.get('security.customPassword.password'));
    }
  }

  updateValidState(): void {
    this.valid = this.formState.useGlobal || this.formState.isValid(this.form);
  }
}
